import React, { useState, useRef, ChangeEvent, FormEvent } from "react";
import { notifications } from "@mantine/notifications";
import { X } from "lucide-react";
import { compressImage } from "Utilities";

export type HistoryFormMode = "add" | "edit" | "cover";

export interface HistoryFormValues {
  year?: number;
  title: string;
  description1: string;
  description2: string;
  description3: string;
  imageUrl?: string | null;
}

interface HistoryFormProps {
  mode: HistoryFormMode;
  initialValues: HistoryFormValues;
  loading?: boolean;
  isDeletingCoverImage?: boolean;
  isDeletingCover?: boolean;
  onSubmit: (
    values: HistoryFormValues,
    file: File | null,
  ) => Promise<void> | void;
  onCancel?: () => void;
  onDeleteImage?: () => Promise<void> | void;
  onDeleteCover?: () => Promise<void> | void;
}

const HistoryForm: React.FC<HistoryFormProps> = ({
  mode,
  initialValues,
  loading = false,
  isDeletingCoverImage = false,
  isDeletingCover = false,
  onSubmit,
  onCancel,
  onDeleteImage,
  onDeleteCover,
}) => {
  const [values, setValues] = useState<HistoryFormValues>(initialValues);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialValues.imageUrl ?? null,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isCoverMode = mode === "cover";
  const [fileKey, setFileKey] = useState(0);
  const yearRef = useRef<HTMLInputElement | null>(null);
  const titleRef = useRef<HTMLInputElement | null>(null);
  const imageRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: name === "year" ? Number(value) : value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const picked = input.files?.[0] ?? null;

    if (!picked) {
      setFile(null);
      setPreviewUrl(values.imageUrl ?? null);
      return;
    }

    const chosen = await compressImage(picked, {
      maxWidth: mode === "cover" ? 1920 : 1200,
    }).catch(() => picked);

    if (chosen.size > MAX_FILE_SIZE) {
      input.value = "";
      setFile(null);
      setPreviewUrl(values.imageUrl ?? null);
      return;
    }

    setErrors((prev) => ({ ...prev, image: "" }));

    setFile(chosen);
    setPreviewUrl(URL.createObjectURL(chosen));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!values.title?.trim()) {
      newErrors.title = "Title is required";
    } else if (values.title.length > 200) {
      newErrors.title = "Title must be 200 characters or less";
    }

    if (!isCoverMode) {
      if (!values.year) {
        newErrors.year = "Year is required";
      } else if (values.year < 1900 || values.year > new Date().getFullYear()) {
        newErrors.year = `Year must be between 1900 and ${new Date().getFullYear()}`;
      }
    }

    // if (isCoverMode && !values.imageUrl && !file) {
    //   newErrors.image = "Image is required";
    // }

    setErrors(newErrors);
    if (newErrors.year && yearRef.current) {
      yearRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      yearRef.current.focus();
    } else if (newErrors.title && titleRef.current) {
      titleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      titleRef.current.focus();
    } else if (newErrors.image && imageRef.current) {
      imageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      imageRef.current.focus();
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setErrors({});
    const result = onSubmit(values, file);

    if (result instanceof Promise) {
      result.finally(() => {
        setFile(null);
        setFileKey((k) => k + 1);
      });
    } else {
      setFile(null);
      setFileKey((k) => k + 1);
    }
  };

  return (
    <>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-blue-600 mb-6"
        >
          ← Back
        </button>
      )}

      <div>
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg border shadow-sm p-6 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* Year */}
      {!isCoverMode && (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium">
        Year<span className="text-red-500">*</span>
      </label>
      <input
        ref={yearRef}
        type="number"
        name="year"
        value={values.year ?? ""}
        min={1900}
        max={new Date().getFullYear()}
        onChange={handleChange}
        className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        placeholder="YYYY"
      />
      {errors.year && (
        <span className="text-xs text-red-600">{errors.year}</span>
      )}
    </div>
  )}

  {/* Title */}
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium">
      Title<span className="text-red-500">*</span>
    </label>

    <input
      ref={titleRef}
      type="text"
      name="title"
      value={values.title}
      maxLength={200}
      onChange={handleChange}
      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
      placeholder="Enter title"
    />

    <div className="text-xs text-gray-500 text-right">
      {values.title.length}/200
    </div>

    {errors.title && (
      <span className="text-xs text-red-600">{errors.title}</span>
    )}
  </div>
</div>


          {/* Description 1 */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">
              Description 1{isCoverMode && "(Hero text)"}
            </label>
            <textarea
              name="description1"
              value={values.description1}
              onChange={handleChange}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[90px]"
            />
          </div>

          {/* Description 2 */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Description 2</label>
            <textarea
              name="description2"
              value={values.description2}
              onChange={handleChange}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[70px]"
            />
          </div>

          {/* Description 3 */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Description 3</label>
            <textarea
              name="description3"
              value={values.description3}
              onChange={handleChange}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[70px]"
            />
          </div>

          {/* Image */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">
              Image
            </label>

            {previewUrl ? (
              <div className="mt-2 relative inline-flex items-start w-fit group overflow-hidden">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="h-28 w-auto rounded border object-cover"
                />
                {onDeleteImage && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!onDeleteImage) return;
                      try {
                        await onDeleteImage();
                        setFile(null);
                        setPreviewUrl(null);
                        setFileKey((k) => k + 1);
                      } catch {
                        // ignore, notifications handled by parent
                      }
                    }}
                    disabled={isDeletingCoverImage}
                    className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    title="Delete cover image"
                  >
                    {isDeletingCoverImage ? (
                      <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <X size={16} />
                    )}
                  </button>
                )}
              </div>
            ) : (
              <div className="h-28 w-40 flex items-center justify-center border rounded text-gray-400 text-sm">
                No Image
              </div>
            )}

            <input
              ref={imageRef}
              key={fileKey}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            <p className="text-xs text-gray-500">
              Max file size: 5MB. Supported formats: JPG, PNG, WebP.
            </p>

            {errors.image && (
              <span className="text-xs text-red-600">{errors.image}</span>
            )}
          </div>

          <div className="sticky bottom-0 -mx-6 mt-6 border-t bg-white px-6 py-4 flex justify-end gap-3 shadow-[0_-2px_6px_rgba(0,0,0,0.05)]">
            {onDeleteCover && (
              <button
                type="button"
                onClick={onDeleteCover}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || isDeletingCover}
              >
                {isDeletingCover ? "Deleting..." : "Delete Cover"}
              </button>
            )}

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                disabled={loading}
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : isCoverMode
                  ? "Save Cover"
                  : mode === "add"
                    ? "Create History"
                    : "Update History"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default HistoryForm;
